import React, { useState, useEffect } from 'react';
import {
  Save,
  Send,
  RotateCcw,
  Mail,
  Paperclip,
  Clock,
  Eye,
  EyeOff,
  Plus,
  X,
  User,
  Calendar,
  Tag,
  Settings,
  AlertCircle,
  CheckCircle,
  Info,
  MousePointer,
  Link,
  BarChart3,
  TrendingUp,
  Users,
  Shield,
  Lock,
  HelpCircle
} from 'lucide-react';
import { Breadcrumb } from '../../components/Common';
import HelpModal from '../../components/HelpModal/HelpModal';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import autoIncrementService from '../../services/autoIncrementService';
import { emailService } from '../../services/emailService';
import { leadGenerationService } from '../../services/leadGenerationService';
import prospectMasterService from '../../services/prospectMasterService';
import customerMasterService from '../../services/customerMasterService';
import { campaignMasterService } from '../../services/campaignMasterService';
import commercialProposalService from '../../services/commercialProposalService';
import { demoSchedulingService } from '../../services/demoSchedulingService';
import '../../styles/components/email-form.css';

const EmailForm = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('compose');
  const [showHelp, setShowHelp] = useState(false);
  const [isLoadingEmailCode, setIsLoadingEmailCode] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const [formData, setFormData] = useState({
    emailCode: '',
    emailType: 'Outbound',
    emailStatus: 'Draft',
    priority: 'Medium',
    subject: '',
    emailBody: '',
    emailBodyHtml: '',
    isHtml: false,
    fromEmail: '',
    fromName: '',
    replyToEmail: '',
    replyToName: '',
    toEmails: [{ email: '', name: '', type: 'To' }],
    ccEmails: [],
    bccEmails: [],
    lead: '',
    prospect: '',
    customer: '',
    campaign: '',
    proposal: '',
    demo: '',
    threadId: '',
    parentEmail: '',
    template: '',
    templateName: '',
    templateVariables: {},
    scheduledDate: '',
    scheduledTime: '',
    timezone: 'Asia/Kolkata',
    isScheduled: false,
    trackingEnabled: true,
    openTracking: true,
    clickTracking: true,
    linkTracking: true,
    attachments: [],
    followUpRequired: false,
    followUpDate: '',
    followUpNotes: '',
    followUpStatus: 'Pending',
    emailOwner: '',
    emailTeam: [],
    assignedTo: '',
    category: '',
    tags: [],
    labels: [],
    encryptionEnabled: false,
    isConfidential: false,
    retentionPeriod: 2555,
    automationRule: '',
    triggerEvent: '',
    triggerConditions: {},
    complianceStatus: 'Compliant',
    gdprCompliant: true,
    consentGiven: false,
    unsubscribeLink: ''
  });

  const [leads, setLeads] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [demos, setDemos] = useState([]);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    loadEmailCode();
    loadMasterData();
  }, []);

  const loadEmailCode = async () => {
    try {
      setIsLoadingEmailCode(true);
      const response = await autoIncrementService.getCurrentValue('EMAIL');
      if (response.success) {
        setFormData((prev) => ({
          ...prev,
          emailCode: response.data.formattedValue,
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          emailCode: 'EM-0001', // Default placeholder
        }));
        console.warn('Auto-increment sequence EMAIL not found, using placeholder');
      }
    } catch (error) {
      console.error('Error loading email code:', error);
      setFormData((prev) => ({
        ...prev,
        emailCode: 'EM-0001', // Default placeholder
      }));
      console.warn('Using placeholder email code due to auto-increment error');
    } finally {
      setIsLoadingEmailCode(false);
    }
  };

  const loadMasterData = async () => {
    try {
      const [
        leadsResponse,
        prospectsResponse,
        customersResponse,
        campaignsResponse,
        proposalsResponse,
        demosResponse
      ] = await Promise.all([
        leadGenerationService.getAllLeads({ limit: 1000 }),
        prospectMasterService.getAllProspects({ limit: 1000 }),
        customerMasterService.getAllCustomers({ limit: 1000 }),
        campaignMasterService.getAllCampaigns({ limit: 1000 }),
        commercialProposalService.getAllProposals({ limit: 1000 }),
        demoSchedulingService.getAllDemos({ limit: 1000 })
      ]);

      if (leadsResponse.success) {
        setLeads(leadsResponse.data.leads || []);
      }
      if (prospectsResponse.success) {
        setProspects(prospectsResponse.data.prospects || []);
      }
      if (customersResponse.success) {
        setCustomers(customersResponse.data.customers || []);
      }
      if (campaignsResponse.success) {
        setCampaigns(campaignsResponse.data.campaigns || []);
      }
      if (proposalsResponse.success) {
        setProposals(proposalsResponse.data.proposals || []);
      }
      if (demosResponse.success) {
        setDemos(demosResponse.data.demos || []);
      }
    } catch (error) {
      console.error('Error loading master data:', error);
      toast.error('Failed to load master data');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleArrayInputChange = (field, index, value) => {
    const newArray = [...formData[field]];
    newArray[index] = value;
    setFormData((prev) => ({
      ...prev,
      [field]: newArray,
    }));
  };

  const handleAddArrayField = (field, defaultItem = '') => {
    setFormData((prev) => ({
      ...prev,
      [field]: [...prev[field], defaultItem],
    }));
  };

  const handleRemoveArrayField = (field, index) => {
    const newArray = formData[field].filter((_, i) => i !== index);
    setFormData((prev) => ({
      ...prev,
      [field]: newArray,
    }));
  };

  const handleAddRecipient = (type) => {
    const newRecipient = { email: '', name: '', type };
    setFormData((prev) => ({
      ...prev,
      [type === 'To' ? 'toEmails' : type === 'CC' ? 'ccEmails' : 'bccEmails']: [
        ...prev[type === 'To' ? 'toEmails' : type === 'CC' ? 'ccEmails' : 'bccEmails'],
        newRecipient
      ],
    }));
  };

  const handleRecipientChange = (type, index, field, value) => {
    const fieldName = type === 'To' ? 'toEmails' : type === 'CC' ? 'ccEmails' : 'bccEmails';
    const newRecipients = [...formData[fieldName]];
    newRecipients[index] = { ...newRecipients[index], [field]: value };
    setFormData((prev) => ({
      ...prev,
      [fieldName]: newRecipients,
    }));
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    const newAttachments = files.map(file => ({
      fileName: file.name,
      filePath: URL.createObjectURL(file), // In real app, upload to server
      fileSize: file.size,
      fileType: file.type,
      mimeType: file.type,
      uploadedAt: new Date()
    }));

    setFormData((prev) => ({
      ...prev,
      attachments: [...prev.attachments, ...newAttachments],
    }));
  };

  const handleRemoveAttachment = (index) => {
    const newAttachments = formData.attachments.filter((_, i) => i !== index);
    setFormData((prev) => ({
      ...prev,
      attachments: newAttachments,
    }));
  };

  const validateEmailData = () => {
    const errors = [];
    
    if (!formData.subject.trim()) {
      errors.push('Subject is required');
    }
    
    if (!formData.emailBody.trim()) {
      errors.push('Email body is required');
    }
    
    if (!formData.fromEmail || !emailService.validateEmailAddress(formData.fromEmail)) {
      errors.push('Valid from email is required');
    }
    
    const validRecipients = formData.toEmails.filter(recipient => 
      recipient.email && emailService.validateEmailAddress(recipient.email)
    );
    
    if (validRecipients.length === 0) {
      errors.push('At least one valid recipient is required');
    }
    
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validateEmailData();
    if (errors.length > 0) {
      toast.error(errors.join(', '));
      return;
    }

    try {
      const emailData = {
        ...formData,
        toEmails: formData.toEmails.filter(recipient => recipient.email.trim() !== ''),
        ccEmails: formData.ccEmails.filter(recipient => recipient.email.trim() !== ''),
        bccEmails: formData.bccEmails.filter(recipient => recipient.email.trim() !== ''),
        tags: formData.tags.filter(tag => tag.trim() !== ''),
        labels: formData.labels.filter(label => label.trim() !== ''),
        emailTeam: formData.emailTeam.filter(team => team.trim() !== ''),
      };

      const response = await emailService.createEmail(emailData);
      if (response.success) {
        toast.success('Email created successfully');
        navigate('/crm/emails');
      } else {
        toast.error(response.message || 'Failed to create email');
      }
    } catch (error) {
      console.error('Error creating email:', error);
      toast.error('Failed to create email');
    }
  };

  const handleSend = async () => {
    const errors = validateEmailData();
    if (errors.length > 0) {
      toast.error(errors.join(', '));
      return;
    }

    try {
      setIsSending(true);
      const emailData = {
        ...formData,
        emailStatus: 'Sent',
        toEmails: formData.toEmails.filter(recipient => recipient.email.trim() !== ''),
        ccEmails: formData.ccEmails.filter(recipient => recipient.email.trim() !== ''),
        bccEmails: formData.bccEmails.filter(recipient => recipient.email.trim() !== ''),
        tags: formData.tags.filter(tag => tag.trim() !== ''),
        labels: formData.labels.filter(label => label.trim() !== ''),
        emailTeam: formData.emailTeam.filter(team => team.trim() !== ''),
      };

      const response = await emailService.createEmail(emailData);
      if (response.success) {
        toast.success('Email sent successfully');
        navigate('/crm/emails');
      } else {
        toast.error(response.message || 'Failed to send email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error('Failed to send email');
    } finally {
      setIsSending(false);
    }
  };

  const handleReset = () => {
    setFormData({
      emailCode: '',
      emailType: 'Outbound',
      emailStatus: 'Draft',
      priority: 'Medium',
      subject: '',
      emailBody: '',
      emailBodyHtml: '',
      isHtml: false,
      fromEmail: '',
      fromName: '',
      replyToEmail: '',
      replyToName: '',
      toEmails: [{ email: '', name: '', type: 'To' }],
      ccEmails: [],
      bccEmails: [],
      lead: '',
      prospect: '',
      customer: '',
      campaign: '',
      proposal: '',
      demo: '',
      threadId: '',
      parentEmail: '',
      template: '',
      templateName: '',
      templateVariables: {},
      scheduledDate: '',
      scheduledTime: '',
      timezone: 'Asia/Kolkata',
      isScheduled: false,
      trackingEnabled: true,
      openTracking: true,
      clickTracking: true,
      linkTracking: true,
      attachments: [],
      followUpRequired: false,
      followUpDate: '',
      followUpNotes: '',
      followUpStatus: 'Pending',
      emailOwner: '',
      emailTeam: [],
      assignedTo: '',
      category: '',
      tags: [],
      labels: [],
      encryptionEnabled: false,
      isConfidential: false,
      retentionPeriod: 2555,
      automationRule: '',
      triggerEvent: '',
      triggerConditions: {},
      complianceStatus: 'Compliant',
      gdprCompliant: true,
      consentGiven: false,
      unsubscribeLink: ''
    });
    loadEmailCode();
  };

  const helpContent = (
    <>
      <h3>What is Compose Email?</h3>
      <p>Compose Email lets you <strong>create and send professional emails</strong> to leads, prospects, customers, and contacts. You can use templates, schedule sending, and track opens/clicks.</p>
      <h3>Tabs</h3>
      <p><strong>Compose</strong> – Subject, body (plain/HTML), from/reply-to, To/CC/BCC recipients, attachments, and link to lead/prospect/customer/campaign/proposal/demo.</p>
      <p><strong>Tracking</strong> – Open and click tracking, analytics.</p>
      <p><strong>Schedule</strong> – Schedule date/time and timezone.</p>
      <h3>How to use</h3>
      <p>Fill subject and body, add recipients. Use <strong>Save Draft</strong> to save or send when ready. Use <strong>Reset</strong> to clear.</p>
    </>
  );

  return (
    <div className="sales-dashboard">
      <Breadcrumb
        items={[
          { label: 'Home', path: '/dashboard' },
          { label: 'CRM', path: '/crm' },
          { label: 'Emails', path: '/crm/emails' },
          { label: 'Compose', isCurrent: true },
        ]}
        rightSlot={
          <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => setShowHelp(true)} title="Help" aria-label="Open help">
            <HelpCircle size={18} />
            <span className="d-none d-sm-inline">Help</span>
          </button>
        }
      />

      <div className="email-form-container">
        <div className="email-form-header">
          <div className="page-title">
            <h1 className="email-form-title">Compose Email</h1>
            <p className="email-form-description">Create and send professional emails to your contacts</p>
          </div>
          <div className="email-form-actions">
            <button
              type="button"
              className="email-form-btn email-form-btn-preview"
              onClick={() => setShowPreview(!showPreview)}
            >
              {showPreview ? <EyeOff size={16} /> : <Eye size={16} />}
              {showPreview ? 'Hide Preview' : 'Preview'}
            </button>
            <button
              type="button"
              className="email-form-btn email-form-btn-secondary"
              onClick={handleReset}
            >
              <RotateCcw size={16} />
              Reset
            </button>
            <button
              type="button"
              className="email-form-btn email-form-btn-primary"
              onClick={handleSend}
              disabled={isSending}
            >
              {isSending ? (
                <>
                  <div className="email-form-spinner"></div>
                  Sending...
                </>
              ) : (
                <>
                  <Send size={16} />
                  Send Email
                </>
              )}
            </button>
          </div>
        </div>

        <div className="email-form-tabs">
          <button
            type="button"
            className={`email-form-tab ${activeTab === 'compose' ? 'active' : ''}`}
            onClick={() => setActiveTab('compose')}
          >
            <Mail size={16} />
            Compose
          </button>
          <button
            type="button"
            className={`email-form-tab ${activeTab === 'recipients' ? 'active' : ''}`}
            onClick={() => setActiveTab('recipients')}
          >
            <User size={16} />
            Recipients
          </button>
          <button
            type="button"
            className={`email-form-tab ${activeTab === 'scheduling' ? 'active' : ''}`}
            onClick={() => setActiveTab('scheduling')}
          >
            <Clock size={16} />
            Scheduling
          </button>
          <button
            type="button"
            className={`email-form-tab ${activeTab === 'tracking' ? 'active' : ''}`}
            onClick={() => setActiveTab('tracking')}
          >
            <Eye size={16} />
            Tracking
          </button>
          <button
            type="button"
            className={`email-form-tab ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            <Settings size={16} />
            Settings
          </button>
        </div>

        <div className="email-form-content">
          <form onSubmit={handleSubmit}>

            {/* Compose Tab */}
            {activeTab === 'compose' && (
              <div className="email-form-section">
                <div className="email-form-section-header">
                  <h3 className="email-form-section-title">Email Composition</h3>
                  <p className="email-form-section-description">Write your email content and configure basic settings</p>
                </div>

                <div className="email-form-grid">
                  <div className="email-form-main">
                    <div className="email-form-group">
                      <label className="email-form-label required">Subject</label>
                      <input
                        type="text"
                        value={formData.subject}
                        onChange={(e) => handleInputChange('subject', e.target.value)}
                        className="email-form-input"
                        placeholder="Enter email subject"
                        required
                      />
                    </div>

                    <div className="email-form-group">
                      <label className="email-form-label required">Email Body</label>
                      <div className="email-format-toggle">
                        <button
                          type="button"
                          className={`email-format-btn ${!formData.isHtml ? 'active' : ''}`}
                          onClick={() => handleInputChange('isHtml', false)}
                        >
                          Plain Text
                        </button>
                        <button
                          type="button"
                          className={`email-format-btn ${formData.isHtml ? 'active' : ''}`}
                          onClick={() => handleInputChange('isHtml', true)}
                        >
                          HTML
                        </button>
                      </div>
                      <textarea
                        value={formData.emailBody}
                        onChange={(e) => handleInputChange('emailBody', e.target.value)}
                        className="email-form-textarea"
                        rows="12"
                        placeholder="Write your email content here..."
                        required
                      />
                    </div>

                    {formData.isHtml && (
                      <div className="email-form-group">
                        <label className="email-form-label">HTML Body</label>
                        <textarea
                          value={formData.emailBodyHtml}
                          onChange={(e) => handleInputChange('emailBodyHtml', e.target.value)}
                          className="email-form-textarea"
                          rows="8"
                          placeholder="Enter HTML content..."
                        />
                      </div>
                    )}
                  </div>

                  <div className="email-form-sidebar">
                    <div className="email-form-group">
                      <label className="email-form-label">Email Type</label>
                      <select
                        value={formData.emailType}
                        onChange={(e) => handleInputChange('emailType', e.target.value)}
                        className="email-form-select"
                      >
                        <option value="Outbound">Outbound</option>
                        <option value="Inbound">Inbound</option>
                        <option value="Follow-up">Follow-up</option>
                        <option value="Marketing">Marketing</option>
                        <option value="Support">Support</option>
                        <option value="Internal">Internal</option>
                        <option value="Automated">Automated</option>
                      </select>
                    </div>

                    <div className="email-form-group">
                      <label className="email-form-label">Priority</label>
                      <select
                        value={formData.priority}
                        onChange={(e) => handleInputChange('priority', e.target.value)}
                        className="email-form-select"
                      >
                        <option value="Low">Low</option>
                        <option value="Medium">Medium</option>
                        <option value="High">High</option>
                      </select>
                    </div>

                    <div className="email-form-group">
                      <label className="email-form-label">Category</label>
                      <select
                        value={formData.category}
                        onChange={(e) => handleInputChange('category', e.target.value)}
                        className="email-form-select"
                    >
                      <option value="">Select Category</option>
                      <option value="Sales">Sales</option>
                      <option value="Marketing">Marketing</option>
                      <option value="Support">Support</option>
                      <option value="Internal">Internal</option>
                      <option value="Follow-up">Follow-up</option>
                      <option value="Proposal">Proposal</option>
                      <option value="Demo">Demo</option>
                      <option value="Contract">Contract</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                    <div className="email-form-group">
                      <label className="email-form-label required">From Email</label>
                      <input
                        type="email"
                        value={formData.fromEmail}
                        onChange={(e) => handleInputChange('fromEmail', e.target.value)}
                        className="email-form-input"
                        placeholder="sender@company.com"
                        required
                      />
                    </div>

                    <div className="email-form-group">
                      <label className="email-form-label">From Name</label>
                      <input
                        type="text"
                        value={formData.fromName}
                        onChange={(e) => handleInputChange('fromName', e.target.value)}
                        className="email-form-input"
                        placeholder="Sender Name"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

          {/* Recipients Tab */}
          {activeTab === 'recipients' && (
            <div className="email-form-section">
              <div className="email-form-section-header">
                <h3 className="email-form-section-title">Recipients</h3>
                <p className="email-form-section-description">Add recipients and configure email distribution</p>
              </div>

              <div className="email-form-grid">
                <div className="email-form-main">
                  {/* To Recipients Card */}
                  <div className="email-recipients-card email-recipients-primary">
                    <div className="email-recipients-header">
                      <div className="email-recipients-title">
                        <User size={20} className="email-recipients-icon" />
                        <h4>To Recipients</h4>
                        <span className="email-recipients-required">*</span>
                      </div>
                      <button
                        type="button"
                        className="email-recipients-add-btn"
                        onClick={() => handleAddRecipient('To')}
                      >
                        <Plus size={16} />
                        Add Recipient
                      </button>
                    </div>
                    <div className="email-recipients-content">
                      {formData.toEmails.map((recipient, index) => (
                        <div key={index} className="email-recipient-item">
                          <div className="email-recipient-type email-recipient-type-to">TO</div>
                          <div className="email-recipient-fields">
                            <input
                              type="email"
                              value={recipient.email}
                              onChange={(e) => handleRecipientChange('To', index, 'email', e.target.value)}
                              className="email-recipient-input"
                              placeholder="recipient@example.com"
                            />
                            <input
                              type="text"
                              value={recipient.name}
                              onChange={(e) => handleRecipientChange('To', index, 'name', e.target.value)}
                              className="email-recipient-input"
                              placeholder="Recipient Name"
                            />
                          </div>
                          {formData.toEmails.length > 1 && (
                            <button
                              type="button"
                              className="email-recipient-remove"
                              onClick={() => handleRemoveArrayField('toEmails', index)}
                            >
                              <X size={16} />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* CC Recipients Card */}
                  <div className="email-recipients-card email-recipients-secondary">
                    <div className="email-recipients-header">
                      <div className="email-recipients-title">
                        <User size={20} className="email-recipients-icon" />
                        <h4>CC Recipients</h4>
                        <span className="email-recipients-optional">Optional</span>
                      </div>
                      <button
                        type="button"
                        className="email-recipients-add-btn email-recipients-add-btn-secondary"
                        onClick={() => handleAddRecipient('CC')}
                      >
                        <Plus size={16} />
                        Add CC
                      </button>
                    </div>
                    <div className="email-recipients-content">
                      {formData.ccEmails.length > 0 ? (
                        formData.ccEmails.map((recipient, index) => (
                          <div key={index} className="email-recipient-item">
                            <div className="email-recipient-type email-recipient-type-cc">CC</div>
                            <div className="email-recipient-fields">
                              <input
                                type="email"
                                value={recipient.email}
                                onChange={(e) => handleRecipientChange('CC', index, 'email', e.target.value)}
                                className="email-recipient-input"
                                placeholder="cc@example.com"
                              />
                              <input
                                type="text"
                                value={recipient.name}
                                onChange={(e) => handleRecipientChange('CC', index, 'name', e.target.value)}
                                className="email-recipient-input"
                                placeholder="CC Name"
                              />
                            </div>
                            <button
                              type="button"
                              className="email-recipient-remove"
                              onClick={() => handleRemoveArrayField('ccEmails', index)}
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ))
                      ) : (
                        <div className="email-recipients-empty">
                          <User size={24} />
                          <span>No CC recipients added</span>
                          <p>Click "Add CC" to include carbon copy recipients</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* BCC Recipients Card */}
                  <div className="email-recipients-card email-recipients-tertiary">
                    <div className="email-recipients-header">
                      <div className="email-recipients-title">
                        <User size={20} className="email-recipients-icon" />
                        <h4>BCC Recipients</h4>
                        <span className="email-recipients-optional">Optional</span>
                      </div>
                      <button
                        type="button"
                        className="email-recipients-add-btn email-recipients-add-btn-tertiary"
                        onClick={() => handleAddRecipient('BCC')}
                      >
                        <Plus size={16} />
                        Add BCC
                      </button>
                    </div>
                    <div className="email-recipients-content">
                      {formData.bccEmails.length > 0 ? (
                        formData.bccEmails.map((recipient, index) => (
                          <div key={index} className="email-recipient-item">
                            <div className="email-recipient-type email-recipient-type-bcc">BCC</div>
                            <div className="email-recipient-fields">
                              <input
                                type="email"
                                value={recipient.email}
                                onChange={(e) => handleRecipientChange('BCC', index, 'email', e.target.value)}
                                className="email-recipient-input"
                                placeholder="bcc@example.com"
                              />
                              <input
                                type="text"
                                value={recipient.name}
                                onChange={(e) => handleRecipientChange('BCC', index, 'name', e.target.value)}
                                className="email-recipient-input"
                                placeholder="BCC Name"
                              />
                            </div>
                            <button
                              type="button"
                              className="email-recipient-remove"
                              onClick={() => handleRemoveArrayField('bccEmails', index)}
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ))
                      ) : (
                        <div className="email-recipients-empty">
                          <User size={24} />
                          <span>No BCC recipients added</span>
                          <p>Click "Add BCC" to include blind carbon copy recipients</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="email-form-sidebar">
                  {/* Recipients Summary */}
                  <div className="email-recipients-summary">
                    <h4 className="email-recipients-summary-title">Recipients Summary</h4>
                    <div className="email-recipients-summary-stats">
                      <div className="email-recipients-stat">
                        <div className="email-recipients-stat-label">To Recipients</div>
                        <div className="email-recipients-stat-value email-recipients-stat-primary">
                          {formData.toEmails.filter(r => r.email.trim() !== '').length}
                        </div>
                      </div>
                      <div className="email-recipients-stat">
                        <div className="email-recipients-stat-label">CC Recipients</div>
                        <div className="email-recipients-stat-value email-recipients-stat-secondary">
                          {formData.ccEmails.filter(r => r.email.trim() !== '').length}
                        </div>
                      </div>
                      <div className="email-recipients-stat">
                        <div className="email-recipients-stat-label">BCC Recipients</div>
                        <div className="email-recipients-stat-value email-recipients-stat-tertiary">
                          {formData.bccEmails.filter(r => r.email.trim() !== '').length}
                        </div>
                      </div>
                      <div className="email-recipients-stat email-recipients-stat-total">
                        <div className="email-recipients-stat-label">Total Recipients</div>
                        <div className="email-recipients-stat-value">
                          {formData.toEmails.filter(r => r.email.trim() !== '').length + 
                           formData.ccEmails.filter(r => r.email.trim() !== '').length + 
                           formData.bccEmails.filter(r => r.email.trim() !== '').length}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="email-recipients-actions">
                    <h4 className="email-recipients-actions-title">Quick Actions</h4>
                    <div className="email-recipients-actions-list">
                      <button
                        type="button"
                        className="email-recipients-action-btn"
                        onClick={() => {
                          const newRecipients = [
                            { email: 'john.doe@company.com', name: 'John Doe', type: 'To' },
                            { email: 'jane.smith@company.com', name: 'Jane Smith', type: 'To' }
                          ];
                          setFormData(prev => ({
                            ...prev,
                            toEmails: [...prev.toEmails, ...newRecipients]
                          }));
                        }}
                      >
                        <User size={16} />
                        Add Sample Recipients
                      </button>
                      <button
                        type="button"
                        className="email-recipients-action-btn"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            ccEmails: [...prev.ccEmails, { email: 'manager@company.com', name: 'Manager', type: 'CC' }]
                          }));
                        }}
                      >
                        <User size={16} />
                        Add Manager to CC
                      </button>
                      <button
                        type="button"
                        className="email-recipients-action-btn"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            bccEmails: [...prev.bccEmails, { email: 'archive@company.com', name: 'Archive', type: 'BCC' }]
                          }));
                        }}
                      >
                        <User size={16} />
                        Add Archive to BCC
                      </button>
                    </div>
                  </div>

                  {/* Recipients Tips */}
                  <div className="email-recipients-tips">
                    <h4 className="email-recipients-tips-title">Recipients Tips</h4>
                    <div className="email-recipients-tips-list">
                      <div className="email-recipients-tip">
                        <CheckCircle size={16} />
                        <span>Use TO for primary recipients</span>
                      </div>
                      <div className="email-recipients-tip">
                        <CheckCircle size={16} />
                        <span>CC for people who need to know</span>
                      </div>
                      <div className="email-recipients-tip">
                        <CheckCircle size={16} />
                        <span>BCC for private copies</span>
                      </div>
                      <div className="email-recipients-tip">
                        <CheckCircle size={16} />
                        <span>Always include at least one TO recipient</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Scheduling Tab */}
          {activeTab === 'scheduling' && (
            <div className="email-form-section">
              <div className="email-form-section-header">
                <h3 className="email-form-section-title">Email Scheduling</h3>
                <p className="email-form-section-description">Schedule your email for later delivery and set up follow-up reminders</p>
              </div>

              <div className="email-form-grid">
                <div className="email-form-main">
                  {/* Email Scheduling Card */}
                  <div className="email-scheduling-card">
                    <div className="email-scheduling-header">
                      <div className="email-scheduling-title">
                        <Clock size={20} className="email-scheduling-icon" />
                        <h4>Schedule Email</h4>
                      </div>
                      <div className="email-scheduling-toggle">
                        <label className="email-scheduling-switch">
                          <input
                            type="checkbox"
                            checked={formData.isScheduled}
                            onChange={(e) => handleInputChange('isScheduled', e.target.checked)}
                          />
                          <span className="email-scheduling-slider"></span>
                        </label>
                        <span className="email-scheduling-label">Enable Scheduling</span>
                      </div>
                    </div>

                    {formData.isScheduled && (
                      <div className="email-scheduling-content">
                        <div className="email-scheduling-grid">
                          <div className="email-form-group">
                            <label className="email-form-label required">Scheduled Date</label>
                            <input
                              type="date"
                              value={formData.scheduledDate}
                              onChange={(e) => handleInputChange('scheduledDate', e.target.value)}
                              className="email-form-input"
                              min={new Date().toISOString().split('T')[0]}
                              required
                            />
                          </div>

                          <div className="email-form-group">
                            <label className="email-form-label required">Scheduled Time</label>
                            <input
                              type="time"
                              value={formData.scheduledTime}
                              onChange={(e) => handleInputChange('scheduledTime', e.target.value)}
                              className="email-form-input"
                              required
                            />
                          </div>

                          <div className="email-form-group">
                            <label className="email-form-label">Timezone</label>
                            <select
                              value={formData.timezone}
                              onChange={(e) => handleInputChange('timezone', e.target.value)}
                              className="email-form-select"
                            >
                              <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                              <option value="America/New_York">America/New_York (EST)</option>
                              <option value="Europe/London">Europe/London (GMT)</option>
                              <option value="Asia/Tokyo">Asia/Tokyo (JST)</option>
                              <option value="America/Los_Angeles">America/Los_Angeles (PST)</option>
                              <option value="Europe/Paris">Europe/Paris (CET)</option>
                            </select>
                          </div>
                        </div>

                        <div className="email-scheduling-preview">
                          <div className="email-scheduling-preview-header">
                            <Calendar size={16} />
                            <span>Scheduled Delivery</span>
                          </div>
                          <div className="email-scheduling-preview-content">
                            {formData.scheduledDate && formData.scheduledTime ? (
                              <div className="email-scheduling-datetime">
                                <div className="email-scheduling-date">
                                  {new Date(`${formData.scheduledDate}T${formData.scheduledTime}`).toLocaleDateString('en-US', {
                                    weekday: 'long',
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                  })}
                                </div>
                                <div className="email-scheduling-time">
                                  {new Date(`${formData.scheduledDate}T${formData.scheduledTime}`).toLocaleTimeString('en-US', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    hour12: true
                                  })} ({formData.timezone.split('/')[1]})
                                </div>
                              </div>
                            ) : (
                              <div className="email-scheduling-empty">
                                <Calendar size={24} />
                                <span>Select date and time to preview</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Follow-up Card */}
                  <div className="email-followup-card">
                    <div className="email-followup-header">
                      <div className="email-followup-title">
                        <AlertCircle size={20} className="email-followup-icon" />
                        <h4>Follow-up Reminder</h4>
                      </div>
                      <div className="email-followup-toggle">
                        <label className="email-followup-switch">
                          <input
                            type="checkbox"
                            checked={formData.followUpRequired}
                            onChange={(e) => handleInputChange('followUpRequired', e.target.checked)}
                          />
                          <span className="email-followup-slider"></span>
                        </label>
                        <span className="email-followup-label">Enable Follow-up</span>
                      </div>
                    </div>

                    {formData.followUpRequired && (
                      <div className="email-followup-content">
                        <div className="email-followup-grid">
                          <div className="email-form-group">
                            <label className="email-form-label required">Follow-up Date</label>
                            <input
                              type="date"
                              value={formData.followUpDate}
                              onChange={(e) => handleInputChange('followUpDate', e.target.value)}
                              className="email-form-input"
                              min={new Date().toISOString().split('T')[0]}
                              required
                            />
                          </div>

                          <div className="email-form-group">
                            <label className="email-form-label">Follow-up Status</label>
                            <select
                              value={formData.followUpStatus}
                              onChange={(e) => handleInputChange('followUpStatus', e.target.value)}
                              className="email-form-select"
                            >
                              <option value="Pending">Pending</option>
                              <option value="In Progress">In Progress</option>
                              <option value="Completed">Completed</option>
                              <option value="Cancelled">Cancelled</option>
                            </select>
                          </div>
                        </div>

                        <div className="email-form-group">
                          <label className="email-form-label">Follow-up Notes</label>
                          <textarea
                            value={formData.followUpNotes}
                            onChange={(e) => handleInputChange('followUpNotes', e.target.value)}
                            className="email-form-textarea"
                            rows="3"
                            placeholder="Add follow-up notes and action items..."
                          />
                        </div>

                        <div className="email-followup-preview">
                          <div className="email-followup-preview-header">
                            <AlertCircle size={16} />
                            <span>Follow-up Reminder</span>
                          </div>
                          <div className="email-followup-preview-content">
                            {formData.followUpDate ? (
                              <div className="email-followup-datetime">
                                <div className="email-followup-date">
                                  {new Date(formData.followUpDate).toLocaleDateString('en-US', {
                                    weekday: 'long',
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                  })}
                                </div>
                                <div className="email-followup-status">
                                  Status: <span className={`email-followup-status-badge ${formData.followUpStatus.toLowerCase().replace(' ', '-')}`}>
                                    {formData.followUpStatus}
                                  </span>
                                </div>
                              </div>
                            ) : (
                              <div className="email-followup-empty">
                                <AlertCircle size={24} />
                                <span>Select follow-up date to preview</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="email-form-sidebar">
                  {/* Quick Actions */}
                  <div className="email-scheduling-actions">
                    <h4 className="email-scheduling-actions-title">Quick Actions</h4>
                    <div className="email-scheduling-actions-list">
                      <button
                        type="button"
                        className="email-scheduling-action-btn"
                        onClick={() => {
                          const tomorrow = new Date();
                          tomorrow.setDate(tomorrow.getDate() + 1);
                          handleInputChange('scheduledDate', tomorrow.toISOString().split('T')[0]);
                          handleInputChange('scheduledTime', '09:00');
                          handleInputChange('isScheduled', true);
                        }}
                      >
                        <Clock size={16} />
                        Schedule for Tomorrow 9 AM
                      </button>
                      <button
                        type="button"
                        className="email-scheduling-action-btn"
                        onClick={() => {
                          const nextWeek = new Date();
                          nextWeek.setDate(nextWeek.getDate() + 7);
                          handleInputChange('scheduledDate', nextWeek.toISOString().split('T')[0]);
                          handleInputChange('scheduledTime', '10:00');
                          handleInputChange('isScheduled', true);
                        }}
                      >
                        <Calendar size={16} />
                        Schedule for Next Week
                      </button>
                      <button
                        type="button"
                        className="email-scheduling-action-btn"
                        onClick={() => {
                          const nextMonth = new Date();
                          nextMonth.setMonth(nextMonth.getMonth() + 1);
                          handleInputChange('followUpDate', nextMonth.toISOString().split('T')[0]);
                          handleInputChange('followUpRequired', true);
                        }}
                      >
                        <AlertCircle size={16} />
                        Set Monthly Follow-up
                      </button>
                    </div>
                  </div>

                  {/* Scheduling Tips */}
                  <div className="email-scheduling-tips">
                    <h4 className="email-scheduling-tips-title">Scheduling Tips</h4>
                    <div className="email-scheduling-tips-list">
                      <div className="email-scheduling-tip">
                        <CheckCircle size={16} />
                        <span>Best times: 9-11 AM or 2-4 PM</span>
                      </div>
                      <div className="email-scheduling-tip">
                        <CheckCircle size={16} />
                        <span>Avoid weekends and holidays</span>
                      </div>
                      <div className="email-scheduling-tip">
                        <CheckCircle size={16} />
                        <span>Consider recipient's timezone</span>
                      </div>
                      <div className="email-scheduling-tip">
                        <CheckCircle size={16} />
                        <span>Set follow-ups for important emails</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tracking Tab */}
          {activeTab === 'tracking' && (
            <div className="email-form-section">
              <div className="email-form-section-header">
                <h3 className="email-form-section-title">Email Tracking</h3>
                <p className="email-form-section-description">Configure email tracking and analytics to measure engagement</p>
              </div>

              <div className="email-form-grid">
                <div className="email-form-main">
                  {/* Tracking Options Card */}
                  <div className="email-tracking-card">
                    <div className="email-tracking-header">
                      <div className="email-tracking-title">
                        <Eye size={20} className="email-tracking-icon" />
                        <h4>Tracking Options</h4>
                      </div>
                      <div className="email-tracking-toggle">
                        <label className="email-tracking-switch">
                          <input
                            type="checkbox"
                            checked={formData.trackingEnabled}
                            onChange={(e) => handleInputChange('trackingEnabled', e.target.checked)}
                          />
                          <span className="email-tracking-slider"></span>
                        </label>
                        <span className="email-tracking-label">Enable Tracking</span>
                      </div>
                    </div>

                    {formData.trackingEnabled && (
                      <div className="email-tracking-content">
                        <div className="email-tracking-options">
                          <div className="email-tracking-option">
                            <label className="email-tracking-option-label">
                              <input
                                type="checkbox"
                                checked={formData.openTracking}
                                onChange={(e) => handleInputChange('openTracking', e.target.checked)}
                                className="email-tracking-checkbox"
                              />
                              <div className="email-tracking-option-content">
                                <div className="email-tracking-option-title">
                                  <Eye size={16} />
                                  Track Email Opens
                                </div>
                                <div className="email-tracking-option-description">
                                  Monitor when recipients open your emails
                                </div>
                              </div>
                            </label>
                          </div>

                          <div className="email-tracking-option">
                            <label className="email-tracking-option-label">
                              <input
                                type="checkbox"
                                checked={formData.clickTracking}
                                onChange={(e) => handleInputChange('clickTracking', e.target.checked)}
                                className="email-tracking-checkbox"
                              />
                              <div className="email-tracking-option-content">
                                <div className="email-tracking-option-title">
                                  <MousePointer size={16} />
                                  Track Link Clicks
                                </div>
                                <div className="email-tracking-option-description">
                                  Monitor clicks on links within your emails
                                </div>
                              </div>
                            </label>
                          </div>

                          <div className="email-tracking-option">
                            <label className="email-tracking-option-label">
                              <input
                                type="checkbox"
                                checked={formData.linkTracking}
                                onChange={(e) => handleInputChange('linkTracking', e.target.checked)}
                                className="email-tracking-checkbox"
                              />
                              <div className="email-tracking-option-content">
                                <div className="email-tracking-option-title">
                                  <Link size={16} />
                                  Track All Links
                                </div>
                                <div className="email-tracking-option-description">
                                  Automatically track all links in email content
                                </div>
                              </div>
                            </label>
                          </div>
                        </div>

                        <div className="email-tracking-preview">
                          <div className="email-tracking-preview-header">
                            <BarChart3 size={16} />
                            <span>Tracking Preview</span>
                          </div>
                          <div className="email-tracking-preview-content">
                            <div className="email-tracking-metrics">
                              <div className="email-tracking-metric">
                                <div className="email-tracking-metric-label">Open Rate</div>
                                <div className="email-tracking-metric-value">
                                  {formData.openTracking ? '24.5%' : 'N/A'}
                                </div>
                              </div>
                              <div className="email-tracking-metric">
                                <div className="email-tracking-metric-label">Click Rate</div>
                                <div className="email-tracking-metric-value">
                                  {formData.clickTracking ? '8.2%' : 'N/A'}
                                </div>
                              </div>
                              <div className="email-tracking-metric">
                                <div className="email-tracking-metric-label">Engagement</div>
                                <div className="email-tracking-metric-value">
                                  {formData.trackingEnabled ? 'High' : 'N/A'}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Reply Configuration Card */}
                  <div className="email-reply-card">
                    <div className="email-reply-header">
                      <div className="email-reply-title">
                        <Mail size={20} className="email-reply-icon" />
                        <h4>Reply Configuration</h4>
                      </div>
                    </div>
                    <div className="email-reply-content">
                      <div className="email-reply-grid">
                        <div className="email-form-group">
                          <label className="email-form-label">Reply-To Email</label>
                          <input
                            type="email"
                            value={formData.replyToEmail}
                            onChange={(e) => handleInputChange('replyToEmail', e.target.value)}
                            className="email-form-input"
                            placeholder="reply@company.com"
                          />
                        </div>

                        <div className="email-form-group">
                          <label className="email-form-label">Reply-To Name</label>
                          <input
                            type="text"
                            value={formData.replyToName}
                            onChange={(e) => handleInputChange('replyToName', e.target.value)}
                            className="email-form-input"
                            placeholder="Reply Name"
                          />
                        </div>
                      </div>

                      <div className="email-form-group">
                        <label className="email-form-label">Unsubscribe Link</label>
                        <input
                          type="url"
                          value={formData.unsubscribeLink}
                          onChange={(e) => handleInputChange('unsubscribeLink', e.target.value)}
                          className="email-form-input"
                          placeholder="https://company.com/unsubscribe"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="email-form-sidebar">
                  {/* Tracking Analytics */}
                  <div className="email-tracking-analytics">
                    <h4 className="email-tracking-analytics-title">Tracking Analytics</h4>
                    <div className="email-tracking-analytics-content">
                      <div className="email-tracking-analytics-item">
                        <div className="email-tracking-analytics-icon">
                          <TrendingUp size={20} />
                        </div>
                        <div className="email-tracking-analytics-info">
                          <div className="email-tracking-analytics-label">Open Rate</div>
                          <div className="email-tracking-analytics-value">24.5%</div>
                        </div>
                      </div>
                      <div className="email-tracking-analytics-item">
                        <div className="email-tracking-analytics-icon">
                          <MousePointer size={20} />
                        </div>
                        <div className="email-tracking-analytics-info">
                          <div className="email-tracking-analytics-label">Click Rate</div>
                          <div className="email-tracking-analytics-value">8.2%</div>
                        </div>
                      </div>
                      <div className="email-tracking-analytics-item">
                        <div className="email-tracking-analytics-icon">
                          <Users size={20} />
                        </div>
                        <div className="email-tracking-analytics-info">
                          <div className="email-tracking-analytics-label">Engaged Users</div>
                          <div className="email-tracking-analytics-value">1,247</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Tracking Benefits */}
                  <div className="email-tracking-benefits">
                    <h4 className="email-tracking-benefits-title">Tracking Benefits</h4>
                    <div className="email-tracking-benefits-list">
                      <div className="email-tracking-benefit">
                        <CheckCircle size={16} />
                        <span>Measure email effectiveness</span>
                      </div>
                      <div className="email-tracking-benefit">
                        <CheckCircle size={16} />
                        <span>Optimize send times</span>
                      </div>
                      <div className="email-tracking-benefit">
                        <CheckCircle size={16} />
                        <span>Improve content quality</span>
                      </div>
                      <div className="email-tracking-benefit">
                        <CheckCircle size={16} />
                        <span>Track engagement patterns</span>
                      </div>
                    </div>
                  </div>

                  {/* Privacy Notice */}
                  <div className="email-tracking-privacy">
                    <h4 className="email-tracking-privacy-title">Privacy & Compliance</h4>
                    <div className="email-tracking-privacy-content">
                      <div className="email-tracking-privacy-item">
                        <Shield size={16} />
                        <span>GDPR Compliant</span>
                      </div>
                      <div className="email-tracking-privacy-item">
                        <Lock size={16} />
                        <span>Data Encrypted</span>
                      </div>
                      <div className="email-tracking-privacy-item">
                        <EyeOff size={16} />
                        <span>Privacy Protected</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="email-form-section">
              <div className="email-form-section-header">
                <h3 className="email-form-section-title">Email Settings</h3>
                <p className="email-form-section-description">Configure advanced email settings and security</p>
              </div>

              <div className="email-form-grid">
                <div className="email-form-main">
                  {/* Security Settings Card */}
                  <div className="email-security-card">
                    <div className="email-security-header">
                      <div className="email-security-title">
                        <Shield size={20} className="email-security-icon" />
                        <h4>Security Settings</h4>
                      </div>
                    </div>
                    <div className="email-security-content">
                      <div className="email-security-options">
                        <div className="email-security-option">
                          <label className="email-security-option-label">
                            <input
                              type="checkbox"
                              checked={formData.encryptionEnabled}
                              onChange={(e) => handleInputChange('encryptionEnabled', e.target.checked)}
                              className="email-security-checkbox"
                            />
                            <div className="email-security-option-content">
                              <div className="email-security-option-title">
                                <Lock size={16} />
                                Enable Encryption
                              </div>
                              <div className="email-security-option-description">
                                Encrypt email content for enhanced security
                              </div>
                            </div>
                          </label>
                        </div>

                        <div className="email-security-option">
                          <label className="email-security-option-label">
                            <input
                              type="checkbox"
                              checked={formData.isConfidential}
                              onChange={(e) => handleInputChange('isConfidential', e.target.checked)}
                              className="email-security-checkbox"
                            />
                            <div className="email-security-option-content">
                              <div className="email-security-option-title">
                                <EyeOff size={16} />
                                Confidential Email
                              </div>
                              <div className="email-security-option-description">
                                Mark email as confidential and restrict access
                              </div>
                            </div>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Compliance Settings Card */}
                  <div className="email-compliance-card">
                    <div className="email-compliance-header">
                      <div className="email-compliance-title">
                        <CheckCircle size={20} className="email-compliance-icon" />
                        <h4>Compliance Settings</h4>
                      </div>
                    </div>
                    <div className="email-compliance-content">
                      <div className="email-compliance-options">
                        <div className="email-compliance-option">
                          <label className="email-compliance-option-label">
                            <input
                              type="checkbox"
                              checked={formData.gdprCompliant}
                              onChange={(e) => handleInputChange('gdprCompliant', e.target.checked)}
                              className="email-compliance-checkbox"
                            />
                            <div className="email-compliance-option-content">
                              <div className="email-compliance-option-title">
                                <Shield size={16} />
                                GDPR Compliant
                              </div>
                              <div className="email-compliance-option-description">
                                Ensure compliance with GDPR regulations
                              </div>
                            </div>
                          </label>
                        </div>

                        <div className="email-compliance-option">
                          <label className="email-compliance-option-label">
                            <input
                              type="checkbox"
                              checked={formData.consentGiven}
                              onChange={(e) => handleInputChange('consentGiven', e.target.checked)}
                              className="email-compliance-checkbox"
                            />
                            <div className="email-compliance-option-content">
                              <div className="email-compliance-option-title">
                                <CheckCircle size={16} />
                                Consent Given
                              </div>
                              <div className="email-compliance-option-description">
                                Confirm recipient consent for data processing
                              </div>
                            </div>
                          </label>
                        </div>
                      </div>

                      <div className="email-compliance-grid">
                        <div className="email-form-group">
                          <label className="email-form-label">Retention Period (Days)</label>
                          <input
                            type="number"
                            value={formData.retentionPeriod}
                            onChange={(e) => handleInputChange('retentionPeriod', parseInt(e.target.value))}
                            className="email-form-input"
                            min="1"
                            max="3650"
                            placeholder="Enter retention period"
                          />
                        </div>

                        <div className="email-form-group">
                          <label className="email-form-label">Compliance Status</label>
                          <select
                            value={formData.complianceStatus}
                            onChange={(e) => handleInputChange('complianceStatus', e.target.value)}
                            className="email-form-select"
                          >
                            <option value="Compliant">Compliant</option>
                            <option value="Non-Compliant">Non-Compliant</option>
                            <option value="Under Review">Under Review</option>
                            <option value="Exempt">Exempt</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="email-form-sidebar">
                  {/* Security Status */}
                  <div className="email-security-status">
                    <h4 className="email-security-status-title">Security Status</h4>
                    <div className="email-security-status-content">
                      <div className="email-security-status-item">
                        <div className="email-security-status-icon">
                          <Shield size={20} />
                        </div>
                        <div className="email-security-status-info">
                          <div className="email-security-status-label">Encryption</div>
                          <div className="email-security-status-value">
                            {formData.encryptionEnabled ? 'Enabled' : 'Disabled'}
                          </div>
                        </div>
                      </div>
                      <div className="email-security-status-item">
                        <div className="email-security-status-icon">
                          <Lock size={20} />
                        </div>
                        <div className="email-security-status-info">
                          <div className="email-security-status-label">Confidentiality</div>
                          <div className="email-security-status-value">
                            {formData.isConfidential ? 'High' : 'Standard'}
                          </div>
                        </div>
                      </div>
                      <div className="email-security-status-item">
                        <div className="email-security-status-icon">
                          <CheckCircle size={20} />
                        </div>
                        <div className="email-security-status-info">
                          <div className="email-security-status-label">Compliance</div>
                          <div className="email-security-status-value">
                            {formData.complianceStatus}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Security Benefits */}
                  <div className="email-security-benefits">
                    <h4 className="email-security-benefits-title">Security Benefits</h4>
                    <div className="email-security-benefits-list">
                      <div className="email-security-benefit">
                        <CheckCircle size={16} />
                        <span>Protect sensitive information</span>
                      </div>
                      <div className="email-security-benefit">
                        <CheckCircle size={16} />
                        <span>Meet regulatory requirements</span>
                      </div>
                      <div className="email-security-benefit">
                        <CheckCircle size={16} />
                        <span>Build customer trust</span>
                      </div>
                      <div className="email-security-benefit">
                        <CheckCircle size={16} />
                        <span>Reduce security risks</span>
                      </div>
                    </div>
                  </div>

                  {/* Compliance Guidelines */}
                  <div className="email-compliance-guidelines">
                    <h4 className="email-compliance-guidelines-title">Compliance Guidelines</h4>
                    <div className="email-compliance-guidelines-content">
                      <div className="email-compliance-guideline">
                        <Info size={16} />
                        <span>GDPR requires explicit consent</span>
                      </div>
                      <div className="email-compliance-guideline">
                        <Info size={16} />
                        <span>Retention periods must be documented</span>
                      </div>
                      <div className="email-compliance-guideline">
                        <Info size={16} />
                        <span>Regular compliance audits recommended</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={handleReset}>
              <RotateCcw size={16} />
              Reset
            </button>
            <button type="submit" className="btn-primary">
              <Save size={16} />
              Save Draft
            </button>
          </div>
          </form>
        </div>
      </div>

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title="Compose Email – Help">
        {helpContent}
      </HelpModal>
    </div>
  );
};

export default EmailForm;
